"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AltiumNetNamingStrategy = void 0;
exports.formatLocalNetNameWithSheetNumber = formatLocalNetNameWithSheetNumber;
exports.appliesChannelNetFormatting = appliesChannelNetFormatting;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const NetCombobulator_1 = require("../../../../NetCombobulation/NetCombobulator");
const altiumProjectFileTypes_1 = require("../../project/altiumProjectFileTypes");
const FallbackNetNamingRule_1 = require("../../../../NetCombobulation/FallbackNetNamingRule");
const NetNamingRuleChain_1 = require("../../../../NetCombobulation/NetNamingRuleChain");
const AltiumHierarchicalGroupingRule_1 = require("./AltiumHierarchicalGroupingRule");
const AltiumPowerPortGroupingRule_1 = require("./AltiumPowerPortGroupingRule");
const AltiumNetLabelGroupingRule_1 = require("./AltiumNetLabelGroupingRule");
const AltiumPortGroupingRule_1 = require("./AltiumPortGroupingRule");
const AltiumOffSheetConnectorGroupingRule_1 = require("./AltiumOffSheetConnectorGroupingRule");
const AltiumHierarchyPriorityNamingRule_1 = require("./AltiumHierarchyPriorityNamingRule");
const AltiumPowerPortNamingRule_1 = require("./AltiumPowerPortNamingRule");
const AltiumNetLabelNamingRule_1 = require("./AltiumNetLabelNamingRule");
const AltiumOffSheetConnectorNamingRule_1 = require("./AltiumOffSheetConnectorNamingRule");
const AltiumPortNamingRule_1 = require("./AltiumPortNamingRule");
const AltiumSheetEntryNamingRule_1 = require("./AltiumSheetEntryNamingRule");
const AltiumNamingPinNamingRule_1 = require("./AltiumNamingPinNamingRule");
const AltiumComponentConnectionNamingRule_1 = require("./AltiumComponentConnectionNamingRule");
const IndividualGroupingRule_1 = require("../../../../NetCombobulation/IndividualGroupingRule");
const AltiumNameGenerator_1 = require("../transformation/AltiumNameGenerator");
const sigma_engine_shared_2 = require("@cadstrom/sigma-engine-shared");
const AltiumSheetEntryLocalRepeatLocalNamingRule_1 = require("./AltiumSheetEntryLocalRepeatLocalNamingRule");
const AltiumLabelTypeNameUtil_1 = require("./AltiumLabelTypeNameUtil");
const AltiumPowerPortLocalNamingRule_1 = require("./AltiumPowerPortLocalNamingRule");
const AltiumHarnessLocalNamingRule_1 = require("./AltiumHarnessLocalNamingRule");
const AltiumLocalNetLabelGroupingRule_1 = require("./AltiumLocalNetLabelGroupingRule");
const AltiumHiddenNetNameGroupingRule_1 = require("./AltiumHiddenNetNameGroupingRule");
const AltiumNetNameCollisionFinalizingRule_1 = require("./AltiumNetNameCollisionFinalizingRule");
const AltiumSheetEntryNamingEligibility_1 = require("./AltiumSheetEntryNamingEligibility");
const SchDocTypes_1 = require("../SchDocTypes");
function formatLocalNetNameWithSheetNumber(baseName, sheetNumber, differentialPairSuffixes = ['_P', '_N']) {
    const terminalDifferentialSuffix = differentialPairSuffixes
        .filter((suffix) => suffix.length > 0)
        .sort((a, b) => b.length - a.length)
        .find((suffix) => {
        if (!baseName.toLowerCase().endsWith(suffix.toLowerCase())) {
            return false;
        }
        return suffix.startsWith('_');
    });
    if (terminalDifferentialSuffix) {
        return `${baseName.slice(0, -terminalDifferentialSuffix.length)}_${sheetNumber}${baseName.slice(-terminalDifferentialSuffix.length)}`;
    }
    return `${baseName}_${sheetNumber}`;
}
function appliesChannelNetFormatting(instance) {
    const inheritedPhysicalContextParent = instance.inheritsPhysicalNamingContext ? instance.path.at(-1) : undefined;
    const numInstances = instance.inheritsPhysicalNamingContext ? 1 : instance.numInstances;
    const totalSheetInstances = instance.inheritsPhysicalNamingContext ?
        (inheritedPhysicalContextParent?.totalSheetInstances ?? instance.totalSheetInstances)
        : instance.totalSheetInstances;
    const designWideInstances = inheritedPhysicalContextParent ? inheritedPhysicalContextParent.designWideInstances : instance.designWideInstances;
    return numInstances !== 1 || (designWideInstances ?? totalSheetInstances) !== 1;
}
class AltiumNetNamingStrategy {
    constructor(projectConfigUtil, nameGenerator, hierarchyMode, options = {}) {
        this.projectConfigUtil = projectConfigUtil;
        this.nameGenerator = nameGenerator;
        const channelRoomNamingStyle = this.projectConfigUtil.getDesignConfiguration('ChannelRoomNamingStyle');
        this.config = {
            mode: hierarchyMode,
            allowPortNetNames: this.projectConfigUtil.getDesignConfigurationBoolean('AllowPortNetNames'),
            allowSheetEntryNetNames: this.projectConfigUtil.getDesignConfigurationBoolean('AllowSheetEntryNetNames'),
            appendSheetNumberToLocalNets: this.projectConfigUtil.getDesignConfigurationBoolean('AppendSheetNumberToLocalNets'),
            powerPortPriority: this.projectConfigUtil.getDesignConfiguration('PowerPortNamesTakePriority'),
            higherLevelNamesTakePriority: this.projectConfigUtil.getDesignConfiguration('NameNetsHierarchically'),
            channelRoomNamingStyle: channelRoomNamingStyle ?? altiumProjectFileTypes_1.ChannelRoomNamingStyle.FlatNumericWithNames,
            differentialPairSuffixes: this.projectConfigUtil.getDifferentialPairSuffixes(),
        };
        this.ruleChain = this.buildRuleChain(options.enableDebug ?? false);
    }
    transformNetName(netObject, instance, localNet) {
        const namingSource = this.getNamingSource(netObject, localNet !== undefined);
        if (!namingSource) {
            return undefined;
        }
        return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName(namingSource.labelType, ...this.applyNamingRules(namingSource.baseName, namingSource.labelType, instance, namingSource.strategyMetadata, localNet));
    }
    transformConnectivityName(netObject, instance, localNet) {
        const namingSource = this.getNamingSource(netObject, localNet !== undefined);
        if (!namingSource ||
            !this.shouldUseSchematicLocalConnectivityName(namingSource.labelType, namingSource.strategyMetadata)) {
            return undefined;
        }
        return this.connectivityName(namingSource.baseName, namingSource.labelType, instance, namingSource.strategyMetadata, localNet);
    }
    canLabelTypeNameNets(labelType, _instance) {
        switch (labelType) {
            case 'port':
                return this.config.allowPortNetNames;
            case 'sheet-entry':
                return this.config.allowSheetEntryNetNames;
            case 'power-port':
                return true;
            case 'net-label':
                return true;
            default:
                return true;
        }
    }
    resolveNetName(net, instance) {
        if (this.config.powerPortPriority === altiumProjectFileTypes_1.PowerPortNamesTakePriorityMode.Yes) {
            const powerLabel = net.labels.find((label) => label.canNameNet && label.labelType === 'power-port', AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOnly);
            if (powerLabel) {
                return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('power-port', this.labelName(powerLabel, instance));
            }
        }
        if (this.config.allowPortNetNames) {
            const portLabel = net.labels.find((label) => label.canNameNet &&
                label.labelType === 'port' &&
                label.strategyMetadata?.harness?.harnessSource !== 'synthetic-port');
            if (portLabel) {
                return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('port', this.labelName(portLabel, instance));
            }
        }
        if (this.config.powerPortPriority === altiumProjectFileTypes_1.PowerPortNamesTakePriorityMode.No) {
            const powerLabel = net.labels.find((label) => label.canNameNet && label.labelType === 'power-port', AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOnly);
            if (powerLabel) {
                return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('power-port', this.labelName(powerLabel, instance));
            }
        }
        const netLabel = net.labels.find((label) => label.canNameNet && label.labelType === 'net-label');
        if (netLabel) {
            if (this.hasRepeatedSheetEntry(net)) {
                const physicalName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('net-label', AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(netLabel.formattedLabel ?? netLabel.originalLabel, 'net-label'));
                if (!this.shouldUseSchematicLocalConnectivityName('net-label', netLabel.strategyMetadata)) {
                    return physicalName;
                }
                const authoredName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('net-label', AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(netLabel.originalLabel, 'net-label'));
                return physicalName === authoredName ? physicalName : `CONNECTIVITY_NAME(${physicalName},${authoredName})`;
            }
            if (this.config.appendSheetNumberToLocalNets && !appliesChannelNetFormatting(instance)) {
                const authoredNetLabel = { originalLabel: netLabel.originalLabel };
                const localPhysicalName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('net-label', ...this.applyNamingRules(authoredNetLabel, 'net-label', instance, netLabel.strategyMetadata, net));
                netLabel.formattedLabel = localPhysicalName;
                if (this.shouldUseSchematicLocalConnectivityName('net-label', netLabel.strategyMetadata)) {
                    return this.connectivityName(authoredNetLabel, 'net-label', instance, netLabel.strategyMetadata, net);
                }
                return localPhysicalName;
            }
            if (this.shouldUseSchematicLocalConnectivityName('net-label', netLabel.strategyMetadata)) {
                return this.connectivityName(netLabel, 'net-label', instance, netLabel.strategyMetadata, net);
            }
            return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('net-label', this.labelName(netLabel, instance));
        }
        const namingConnection = net.connections.find((conn) => conn.connection.appliesNetName);
        if (namingConnection?.connection.appliesNetName) {
            return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('NamingPin', namingConnection.connection.appliesNetName.label);
        }
        if (net.connections.length > 0) {
            const firstConn = net.connections[0];
            const moduleName = firstConn.connection.moduleName;
            const moduleRef = firstConn.connection.moduleRef;
            return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('ComponentConnection', `Net${moduleName}_${firstConn.connection.pinRef}`, moduleRef.length > 0 ? moduleRef : undefined);
        }
        if (this.config.allowSheetEntryNetNames) {
            const sheetEntryLabel = net.labels.find((label) => label.canNameNet &&
                label.labelType === 'sheet-entry' &&
                label.strategyMetadata?.harness?.harnessSource !== 'synthetic-sheet-entry');
            if (sheetEntryLabel) {
                const strategyMetadata = sheetEntryLabel.strategyMetadata;
                if (!strategyMetadata || !strategyMetadata['sheet-entry-metadata']) {
                    throw new Error(`Sheet entry label is missing metadata`);
                }
                return AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName('sheet-entry', this.labelName(sheetEntryLabel, instance, 'sheet-entry'), strategyMetadata['sheet-entry-metadata'].sheetSymbolUniqueId);
            }
        }
        return `NET_UNKNOWN_${sigma_engine_shared_2.Utils.randomAlphaStr(10)}`;
    }
    labelName(label, instance, ensureType, componentSuffix, selectionMode = AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOrChannel, localNetSheetNumber) {
        const derefed = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(label.formattedLabel ?? label.originalLabel, ensureType);
        const useComponentName = localNetSheetNumber === undefined ?
            `${derefed}${componentSuffix ?? ''}`
            : formatLocalNetNameWithSheetNumber(derefed, localNetSheetNumber, this.config.differentialPairSuffixes?.length ? this.config.differentialPairSuffixes : undefined);
        if (selectionMode === AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOnly) {
            return useComponentName;
        }
        const inheritedPhysicalContextParent = instance.inheritsPhysicalNamingContext ? instance.path.at(-1) : undefined;
        const roomName = instance.inheritsPhysicalNamingContext ? (instance.physicalRoomName ?? instance.roomName) : instance.roomName;
        const channelIndex = instance.inheritsPhysicalNamingContext ?
            (instance.physicalChannelIndex ?? instance.channelIndex)
            : instance.channelIndex;
        const physicalChannelIndex = instance.physicalChannelIndex ?? instance.channelIndex;
        const channelPrefix = instance.inheritsPhysicalNamingContext ?
            (instance.physicalChannelPrefix ?? instance.channelPrefix)
            : instance.channelPrefix;
        return this.nameGenerator.generateName(useComponentName, {
            name: roomName,
            filename: instance.hierarchyEntry.sourceFile,
            uniqueId: instance.hierarchyEntry.uniqueId ?? '<unknown-uniqueid>',
            channelIndex,
            physicalChannelIndex,
            roomName,
            channelPrefix,
            pathUniqueId: instance.pathUniqueId,
            isMultiChannel: instance.isMultiChannel,
            numInstances: instance.inheritsPhysicalNamingContext ? 1 : instance.numInstances,
            totalSheetInstances: instance.inheritsPhysicalNamingContext ?
                (inheritedPhysicalContextParent?.totalSheetInstances ?? instance.totalSheetInstances)
                : instance.totalSheetInstances,
            designWideInstances: inheritedPhysicalContextParent ?
                inheritedPhysicalContextParent.designWideInstances
                : instance.designWideInstances,
        }, {}, selectionMode, AltiumNameGenerator_1.NameGeneratorDesignatorType.Physical, AltiumNameGenerator_1.NameGeneratorNameType.Net);
    }
    formatChannelLocalPowerPortName(label, instance) {
        return this.nameGenerator.generateName(AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(label.originalLabel), {
            name: instance.roomName,
            filename: instance.hierarchyEntry.sourceFile,
            uniqueId: instance.hierarchyEntry.uniqueId ?? '<unknown-uniqueid>',
            channelIndex: instance.channelIndex,
            physicalChannelIndex: instance.physicalChannelIndex ?? instance.channelIndex,
            roomName: instance.roomName,
            channelPrefix: instance.channelPrefix,
            pathUniqueId: instance.pathUniqueId,
            isMultiChannel: instance.isMultiChannel,
            numInstances: instance.numInstances,
            totalSheetInstances: instance.totalSheetInstances,
        }, {}, AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOrChannel, AltiumNameGenerator_1.NameGeneratorDesignatorType.Physical, AltiumNameGenerator_1.NameGeneratorNameType.Net);
    }
    getNamingSource(netObject, useOriginalLabel = false) {
        if (netObject.label?.canNameNet) {
            return {
                baseName: useOriginalLabel ? { originalLabel: netObject.label.originalLabel } : netObject.label,
                labelType: netObject.label.labelType,
                strategyMetadata: netObject.label.strategyMetadata,
            };
        }
        if (netObject.connection?.connection.appliesNetName) {
            return {
                baseName: { originalLabel: netObject.connection.connection.appliesNetName.label },
                labelType: netObject.connection.connection.appliesNetName.labelType,
                strategyMetadata: netObject.connection.connection.appliesNetName.strategyMetadata,
            };
        }
        return undefined;
    }
    shouldUseSchematicLocalConnectivityName(labelType, strategyMetadata) {
        return (labelType === 'net-label' &&
            !strategyMetadata?.harness?.preservePhysicalName &&
            (this.config.mode === altiumProjectFileTypes_1.HierarchyMode.Hierarchical || this.config.mode === altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical));
    }
    resolvePhysicalNetNames(hierarchy) {
        return this.ruleChain.execute(hierarchy);
    }
    getNetNamingDebug() {
        const debug = this.ruleChain.exportDebugInfo();
        if (!debug) {
            return undefined;
        }
        return {
            enabled: true,
            snapshots: debug.snapshots,
            trackedObjects: Object.fromEntries(debug.trackedObjects),
        };
    }
    createVirtualRootHierarchy(roots) {
        if (roots.length === 0) {
            throw new Error('createVirtualRootHierarchy requires at least one root');
        }
        const firstChildInstance = roots[0].instance;
        const virtualInstance = {
            instanceId: '__virtual_root__',
            hierarchyEntry: firstChildInstance.hierarchyEntry,
            path: [],
            children: roots.map((c) => c.instance),
            pathUniqueId: '__virtual_root__',
            sheetSymbol: undefined,
            numInstances: 1,
            totalSheetInstances: 1,
            instanceIndex: 0,
            channelIndex: 0,
            channelPrefix: '',
            roomName: '',
            isMultiChannel: false,
        };
        const emptyCombobulator = new NetCombobulator_1.NetCombobulator(new sigma_engine_shared_1.NetRefCtx(), this, virtualInstance, {
            autoAssignNets: false,
            pointRoundingPrecision: 0.1,
        });
        return {
            pathUniqueId: '__virtual_root__',
            hierarchyDepth: -1,
            instance: virtualInstance,
            combobulator: emptyCombobulator,
            children: roots,
        };
    }
    buildRuleChain(enableDebug) {
        const chain = new NetNamingRuleChain_1.NetNamingRuleChain(enableDebug);
        chain.add(new AltiumHarnessLocalNamingRule_1.AltiumHarnessLocalNamingRule(this.config, this));
        chain.add(new AltiumSheetEntryLocalRepeatLocalNamingRule_1.AltiumSheetEntryLocalRepeatLocalNamingRule(this.config, this));
        chain.add(new AltiumPowerPortLocalNamingRule_1.AltiumPowerPortLocalNamingRule(this.config, this));
        chain.add(new IndividualGroupingRule_1.IndividualGroupingRule());
        this.addGroupingRules(chain);
        this.addNamingRules(chain);
        return chain;
    }
    addGroupingRules(chain) {
        chain.add(new AltiumLocalNetLabelGroupingRule_1.AltiumLocalNetLabelGroupingRule());
        chain.add(new AltiumHiddenNetNameGroupingRule_1.AltiumHiddenNetNameGroupingRule(this.config));
        if (this.config.mode === altiumProjectFileTypes_1.HierarchyMode.Hierarchical || this.config.mode === altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical) {
            chain.add(new AltiumHierarchicalGroupingRule_1.AltiumHierarchicalGroupingRule(this.config));
        }
        chain.add(new AltiumPowerPortGroupingRule_1.AltiumPowerPortGroupingRule(this.config));
        if (this.config.mode === altiumProjectFileTypes_1.HierarchyMode.Global) {
            chain.add(new AltiumNetLabelGroupingRule_1.AltiumNetLabelGroupingRule(this.config));
        }
        if (this.config.mode === altiumProjectFileTypes_1.HierarchyMode.Flat || this.config.mode === altiumProjectFileTypes_1.HierarchyMode.Global) {
            chain.add(new AltiumPortGroupingRule_1.AltiumPortGroupingRule(this.config));
        }
        chain.add(new AltiumOffSheetConnectorGroupingRule_1.AltiumOffSheetConnectorGroupingRule(this.config));
    }
    addNamingRules(chain) {
        if (this.config.higherLevelNamesTakePriority) {
            chain.add(new AltiumHierarchyPriorityNamingRule_1.AltiumHierarchyPriorityNamingRule(this.config, this));
        }
        if (this.config.powerPortPriority === altiumProjectFileTypes_1.PowerPortNamesTakePriorityMode.Yes) {
            chain.add(new AltiumPowerPortNamingRule_1.AltiumPowerPortNamingRule(this.config, this));
        }
        chain.add(new AltiumNetLabelNamingRule_1.AltiumNetLabelNamingRule(this.config, this));
        chain.add(new AltiumNamingPinNamingRule_1.AltiumNamingPinNamingRule(this.config, this));
        if (this.config.powerPortPriority === altiumProjectFileTypes_1.PowerPortNamesTakePriorityMode.No) {
            chain.add(new AltiumPowerPortNamingRule_1.AltiumPowerPortNamingRule(this.config, this));
        }
        chain.add(new AltiumSheetEntryNamingRule_1.AltiumSheetEntryNamingRule(this.config, this));
        chain.add(new AltiumOffSheetConnectorNamingRule_1.AltiumOffSheetConnectorNamingRule(this.config, this));
        chain.add(new AltiumPortNamingRule_1.AltiumPortNamingRule(this.config, this));
        chain.add(new AltiumComponentConnectionNamingRule_1.AltiumComponentConnectionNamingRule(this.config, this));
        chain.add(new FallbackNetNamingRule_1.FallbackNetNamingRule('NET_UNKNOWN'));
        chain.add(new AltiumNetNameCollisionFinalizingRule_1.AltiumNetNameCollisionFinalizingRule());
    }
    applyNamingRules(baseName, labelType, instance, strategyMetadata, localNet) {
        if (labelType === 'net-label' && strategyMetadata?.harness?.preservePhysicalName) {
            return [
                this.labelName(baseName, instance, labelType, undefined, AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOnly),
            ];
        }
        if (labelType === 'power-port' && this.config.mode !== altiumProjectFileTypes_1.HierarchyMode.StrictHierarchical) {
            return [
                this.labelName(baseName, instance, labelType, undefined, AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOnly),
            ];
        }
        const localNetSheetNumber = this.shouldAppendSheetNumberToLocalNetLabel(labelType, instance, localNet) ?
            this.getLocalNetSheetNumber(instance)
            : undefined;
        const netName = this.labelName(baseName, instance, labelType, undefined, undefined, localNetSheetNumber);
        if (labelType === 'sheet-entry') {
            return [netName, strategyMetadata?.['sheet-entry-metadata']?.sheetSymbolUniqueId];
        }
        return [netName];
    }
    connectivityName(baseName, labelType, instance, strategyMetadata, localNet) {
        const physicalName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName(labelType, ...this.applyNamingRules(baseName, labelType, instance, strategyMetadata, localNet));
        const localName = AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.getLabelTypeName(labelType, ...this.applyConnectivityNamingRules(baseName, labelType, instance, strategyMetadata, localNet));
        if (physicalName === localName) {
            return localName;
        }
        return `CONNECTIVITY_NAME(${physicalName},${localName})`;
    }
    applyConnectivityNamingRules(baseName, labelType, instance, _strategyMetadata, localNet) {
        const localNetSheetNumber = this.shouldAppendSheetNumberToLocalNetLabel(labelType, instance, localNet) ?
            this.getLocalNetSheetNumber(instance)
            : undefined;
        const netName = this.labelName({ originalLabel: baseName.originalLabel }, instance, labelType, undefined, AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOnly, localNetSheetNumber);
        return [netName];
    }
    getHierarchicalNetLabelName(label, localNet, instance, group) {
        const formattedName = label.formattedLabel ?
            AltiumLabelTypeNameUtil_1.AltiumLabelTypeNameUtil.maybeDereferenceLabelName(label.formattedLabel)
            : label.originalLabel;
        if (group.hasLabelType('port') &&
            group
                .getNetLabels()
                .some((candidate) => candidate.labelType === 'net-label' &&
                candidate.strategyMetadata?.busRangeMember === true &&
                candidate.originalLabel.toLowerCase() === label.originalLabel.toLowerCase())) {
            return label.originalLabel;
        }
        if (formattedName !== label.originalLabel &&
            group
                .getNetLabels()
                .some((candidate) => candidate.labelType === 'net-label' &&
                candidate.strategyMetadata?.busRangeMember === true &&
                candidate.originalLabel.toLowerCase() === formattedName.toLowerCase())) {
            return formattedName;
        }
        if (this.isNonLocalConnectedLocalNetLabel(localNet, instance) ||
            this.hasGroupReachableIdentityPortFromChildLabel(label, localNet, instance, group)) {
            return this.labelName({ originalLabel: label.originalLabel }, instance, 'net-label', undefined, AltiumNameGenerator_1.NameGeneratorAnnotationSelectionMode.ExplicitOnly);
        }
        return label.formattedLabel ?? label.originalLabel;
    }
    getNetLabelNameForNamingGroup(label, instance, group) {
        if (group.hasLabelType('port') &&
            group
                .getNetLabels()
                .some((candidate) => candidate.labelType === 'net-label' &&
                candidate.strategyMetadata?.busRangeMember === true &&
                candidate.originalLabel.toLowerCase() === label.originalLabel.toLowerCase())) {
            return label.originalLabel;
        }
        if (this.config.appendSheetNumberToLocalNets &&
            !appliesChannelNetFormatting(instance) &&
            this.hasPowerPortOnInstance(instance, group)) {
            return label.originalLabel;
        }
        return label.formattedLabel ?? label.originalLabel;
    }
    hasPowerPortOnInstance(instance, group) {
        return (group.getNets().get(instance) ?? []).some((net) => net.labels.some((label) => label.labelType === 'power-port'));
    }
    shouldAppendSheetNumberToLocalNetLabel(labelType, instance, localNet) {
        return (this.config.appendSheetNumberToLocalNets &&
            labelType === 'net-label' &&
            !appliesChannelNetFormatting(instance) &&
            !this.hasRepeatedSheetEntry(localNet) &&
            !localNet?.labels.some((label) => label.labelType === 'port' || label.labelType === 'power-port'));
    }
    getLocalNetSheetNumber(instance) {
        if (this.projectConfigUtil.getDesignConfigurationBoolean('AutoSheetNumbering')) {
            return this.projectConfigUtil.getDesignConfigurationBoolean('ReorderDocumentsOnCompile') ?
                instance.compiledSheetNumber
                : instance.hierarchyEntry.sheetNumber;
        }
        const schematic = instance.hierarchyEntry.schematic;
        if (!schematic || typeof schematic.getChildRecords !== 'function') {
            return instance.hierarchyEntry.sheetNumber;
        }
        const sheetNumberParameter = schematic
            .getChildRecords(-1)
            .filter((record) => record.recordType === SchDocTypes_1.RecordType.PARAMETER)
            .find((record) => record.name.toLowerCase() === 'sheetnumber');
        const value = sheetNumberParameter?.text.trim();
        if (!value || value === '*') {
            return undefined;
        }
        const sheetNumber = Number(value);
        return Number.isInteger(sheetNumber) && sheetNumber > 0 ? sheetNumber : undefined;
    }
    hasRepeatedSheetEntry(localNet) {
        return (localNet?.labels.some((label) => label.labelType === 'sheet-entry' && label.strategyMetadata?.['sheet-entry-metadata']?.isRepeated) ?? false);
    }
    hasGroupReachableIdentityPortFromChildLabel(winningLabel, winningNet, instance, group) {
        if (instance.path.length === 0) {
            return false;
        }
        for (const [portInstance, nets] of group.getNets()) {
            if (portInstance !== instance && !instance.path.includes(portInstance)) {
                continue;
            }
            for (const net of nets) {
                if (net === winningNet) {
                    continue;
                }
                for (const port of net.labels) {
                    if (port.labelType === 'port' &&
                        (0, AltiumSheetEntryNamingEligibility_1.isAltiumPairedChildPortNamingEligible)(group, port, this.config.mode) &&
                        this.isHierarchicalIdentityPort(winningLabel, port)) {
                        return true;
                    }
                }
            }
        }
        return false;
    }
    isHierarchicalIdentityPort(winningLabel, port) {
        if (winningLabel.originalLabel.toLowerCase() === port.originalLabel.toLowerCase()) {
            return true;
        }
        const portMemberName = port.strategyMetadata?.harness?.harnessMemberName;
        if (!portMemberName) {
            return false;
        }
        const winningMemberName = winningLabel.strategyMetadata?.harness?.harnessMemberName ?? winningLabel.originalLabel.split('.').at(-1);
        return winningMemberName?.toLowerCase() === portMemberName.toLowerCase();
    }
    isNonLocalConnectedLocalNetLabel(localNet, instance) {
        return (this.config.appendSheetNumberToLocalNets &&
            !appliesChannelNetFormatting(instance) &&
            localNet.labels.some((label) => label.labelType === 'port' || label.labelType === 'power-port'));
    }
}
exports.AltiumNetNamingStrategy = AltiumNetNamingStrategy;
//# sourceMappingURL=AltiumNetNamingStrategy.js.map